import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import {
    CloseCircleIcon,
    ProgressBar,
} from '@leanpay/ui'

import { isMarketSlo, isProdEnvironment } from 'library/constants'
import { shouldShowProgressBar } from 'library/utils'
import LpLogoImg from 'assets/lp_logo.png'

import ExitConfirmationDialog from '../exit-confirmation-dialog/exit-confirmation-dialog'

const languageNames = {
    en: 'English',
    ro: 'română',
    sl: 'slovenščina',
}

const LanguageSwitcher = () => {
    const { t, i18n } = useTranslation()
    const marketLanguage = isMarketSlo ? 'sl' : 'ro'
    const marketLanguageLabel = isMarketSlo ? 'SI' : 'RO'
    const marketLanguageName = languageNames[marketLanguage]

    if (isProdEnvironment) {
        return null
    }

    return (
        <div className="flex gap-1">
            <button
                onClick={() => {
                    void i18n.changeLanguage('en')
                }}
                className={`inline-flex items-center justify-center rounded-md text-xs font-semibold uppercase w-8 h-8 p-0 border-[1px] border-black transition-colors hover:bg-gray-50 shadow-[0_2px_4px_rgba(0,0,0,0.08)] ${i18n.language === 'en' ? 'bg-lp-peach1' : 'bg-lp-grey1'} text-lp-dark1`}
                aria-label={
                    i18n.language === 'en'
                        ? `${t('common.switchToLanguage', { language: languageNames.en })} (${t('common.currentlySelected')})`
                        : t('common.switchToLanguage', {
                              language: languageNames.en,
                          })
                }
            >
                EN
            </button>
            <button
                onClick={() => {
                    void i18n.changeLanguage(marketLanguage)
                }}
                className={`inline-flex items-center justify-center rounded-md text-xs font-semibold uppercase w-8 h-8 p-0 border-[1px] border-black transition-colors hover:bg-gray-50 shadow-[0_2px_4px_rgba(0,0,0,0.08)] ${i18n.language === marketLanguage ? 'bg-lp-peach1' : 'bg-lp-grey1'} text-lp-dark1`}
                aria-label={
                    i18n.language === marketLanguage
                        ? `${t('common.switchToLanguage', { language: marketLanguageName })} (${t('common.currentlySelected')})`
                        : t('common.switchToLanguage', {
                              language: marketLanguageName,
                          })
                }
            >
                {marketLanguageLabel}
            </button>
        </div>
    )
}

interface CreditHeaderProps {
    progress: number
    showCancelIcon?: boolean
    children: ReactNode
}

const CreditHeader = ({ progress, showCancelIcon = false, children }: CreditHeaderProps) => {
    const { t } = useTranslation()
    const showProgressbar = shouldShowProgressBar()

    return (
        <>
            <div
                className="flex flex-col max-w-[840px] w-full sticky items-center gap-1 max-[766px]:px-2 min-[767px]:px-4 pt-1 rounded-lg max-[766px]:w-full max-[766px]:rounded-none min-[767px]:max-[899px]:rounded-none min-[767px]:max-[899px]:w-full"
                style={{
                    background: 'linear-gradient(0deg, rgba(2,0,36,0) 0%, rgba(243,246,248,1) 58%)',
                }}
            >
                {showProgressbar && <ProgressBar progress={progress} className="w-full" />}

                <div className="flex justify-between items-center w-full">
                    <div className="z-[1000] flex flex-1">
                        <LanguageSwitcher />
                    </div>
                    <div className="flex flex-1 items-center text-center justify-center">
                        <img
                            src={LpLogoImg}
                            className="w-[126px] h-[37px]"
                            alt="Leanpay Logo"
                        />
                    </div>
                    <div className="flex-1 flex justify-end">
                        {showCancelIcon && (
                            <ExitConfirmationDialog>
                                <button
                                    className="cursor-pointer bg-transparent rounded-full"
                                    aria-label={t('common.leave')}
                                >
                                    <CloseCircleIcon />
                                </button>
                            </ExitConfirmationDialog>
                        )}
                    </div>
                </div>
            </div>

            {children}
        </>
    )
}

export default CreditHeader
